import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useRef, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import { MenuItem } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/store/store';
import { useGetThreadQuery, usePostCommentMutation } from 'src/services/threads/threads.service';
import { ThreadType, CommentType, ICommentData, CommentAccount } from 'src/services/threads/threads.types';
import { useGetOrganizationNoteTypesQuery } from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';
import FormProvider, { RHFSelect, RHFTextField, RHFMentionTextField } from 'src/components/hook-form';

import { TenantType } from 'src/types/enums';

// ----------------------------------------------------------------------

type Props = {
  thread_id?: string;
  disabled?: boolean;
};

export default function PostCommentForm({ thread_id, disabled }: Props) {

  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const tenantType = useOrgTenant();

  const { organization } = useAuthContext();

  const [postComment] = usePostCommentMutation();

  const users = useAppSelector((state) => state?.data?.users);

  const taggedAccountsRef = useRef<CommentAccount[]>([]);

  const { currentData: thread, isLoading: isLoadingThread } = useGetThreadQuery({
    thread_id: thread_id as string
  }, {
    skip: !thread_id
  });

  // JT1-1596: the type list is the organisation's own, not a hardcoded enum. A candidate tenant has
  // no organisation, so it posts untyped and the server files the note as General.
  const { currentData: noteTypes } = useGetOrganizationNoteTypesQuery(
    { org_id: organization?.id },
    { skip: !organization?.id }
  );

  const noteTypeOptions = useMemo(() => noteTypes ?? [], [noteTypes]);

  const defaultNoteTypeId = useMemo(() => {
    const general = noteTypeOptions.find((noteType) => noteType.system_key === 'general');
    return general?.id ?? noteTypeOptions[0]?.id ?? '';
  }, [noteTypeOptions]);

  const CommentSchema = Yup.object().shape({
    text: Yup.string().required('Comment is required'),
    note_type_id: Yup.string().optional(),
  });

  const defaultValues = {
    text: '',
    note_type_id: ''
  };

  const methods = useForm({
    resolver: yupResolver(CommentSchema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methods;

  // Preselect General once the types arrive, matching what the dropdown has always defaulted to.
  const selectedNoteTypeId = watch('note_type_id');

  useEffect(() => {
    if (!selectedNoteTypeId && defaultNoteTypeId) {
      setValue('note_type_id', defaultNoteTypeId);
    }
  }, [defaultNoteTypeId, selectedNoteTypeId, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    try {

      if (!thread) return;

      const commentData: ICommentData = {
        tagged_accounts: taggedAccountsRef.current,
      };

      let type: CommentType = CommentType.TEXT;

      switch (thread.type) {
        case ThreadType.CHAT:
          commentData.text = data.text;
          type = CommentType.TEXT;
          break;
        case ThreadType.NOTES:
          commentData.activity = {
            note_type_id: data.note_type_id || undefined,
            text: data.text,
          };
          type = CommentType.ACTIVITY;
          break;
        default:
          commentData.text = data.text;
          type = CommentType.TEXT;
          break;
      }

      await postComment({
        thread_id: thread_id as string,
        type,
        data: commentData
      }).unwrap();

      resetForm();
    } catch (error) {
      console.error(error);

      enqueueSnackbar(t('threads.api.post.default_error'), { variant: 'error' });
    }
  });

  const buttonText = useMemo(() => {
    if (thread?.type === ThreadType.CHAT) {
      return t('threads.form.submit_comment');
    }

    if (thread?.type === ThreadType.NOTES) {
      return t('threads.form.submit_note');
    }

    return t('threads.form.submit_comment');
  }, [thread, t]);

  const resetForm = () => reset({ text: '', note_type_id: defaultNoteTypeId });

  // JT1-1561: chat only. In a NOTES thread Enter has to insert a newline - people type multi-line
  // notes and hit Enter expecting one, and the posted note could not be edited or deleted by a
  // non-admin author. Notes are saved from the 'Add Note' button alone.
  const onChatKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  }

  const handleMentionChange = (accounts: CommentAccount[]) => {
    taggedAccountsRef.current = accounts || [];
  }

  const userOptions = useMemo(() => users?.map((user) => ({
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
  })) || [], [users]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={2}>

        {
          thread?.type === ThreadType.NOTES && (
            <Stack direction="row" justifyContent="flex-end" gap={1}>

              {noteTypeOptions.length > 0 && (
                <RHFSelect
                  size='small'
                  name="note_type_id"
                  label={t('threads.form.activity_type.label')}
                  sx={{ flexGrow: 1 }}
                  placeholder={t('threads.form.activity_type.placeholder')}
                >
                  {noteTypeOptions.map((noteType) => (
                    <MenuItem key={noteType.id} value={noteType.id}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Iconify icon={noteType.icon} sx={{ color: noteType.colour }} />
                        {noteType.name}
                      </Stack>
                    </MenuItem>
                  ))}
                </RHFSelect>
              )}

              <LoadingButton
                type="submit"
                variant="contained"
                sx={{ flexShrink: 0, width: 200 }}
                disabled={!thread || !isValid || disabled || isLoadingThread}
                loading={isSubmitting}
              >
                {buttonText}
              </LoadingButton>
            </Stack>
          )
        }

        {
          thread?.type === ThreadType.CHAT && (
            <RHFTextField
              name="text"
              placeholder={t('threads.form.comment.placeholder')}
              multiline
              onKeyDown={onChatKeyDown}
              disabled={disabled}
              rows={4}
            />
          )
        }

        {
          thread?.type === ThreadType.NOTES && (
            <>
              {

                tenantType === TenantType.Candidate ? (
                  <RHFTextField
                    name="text"
                    placeholder={t('threads.form.comment.placeholder')}
                    multiline
                    disabled={disabled}
                    rows={4}
                  />
                ) : (
                  <RHFMentionTextField
                    name="text"
                    placeholder={t('threads.form.comment.placeholder')}
                    multiline
                    disabled={disabled}
                    rows={4}
                    onMentionChange={handleMentionChange}
                    users={userOptions as any[]}
                  />
                )
              }
            </>
          )
        }

        {
          thread?.type === ThreadType.CHAT && (
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <LoadingButton
                type="submit"
                variant="contained"
                sx={{ flexShrink: 0, width: 200 }}
                disabled={!thread || !isValid || disabled || isLoadingThread}
                loading={isSubmitting}
              >
                {buttonText}
              </LoadingButton>
            </Stack>
          )
        }

      </Stack>
    </FormProvider>
  );
}
